import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { DASHBOARD_ROUTES } from '@/api/constants/app.url';

interface Crumb {
    label: string;
    to?:    string;
}

interface Props {
    /**
     * Optional trailing crumbs appended after the URL-derived trail.
     * Use for dynamic context the URL can't supply on its own — e.g. a
     * property's address on `/properties/:id`.
     */
    extra?: Crumb[];
}

/**
 * URL-driven breadcrumb trail. Splits the current pathname into segments,
 * maps each segment to a friendly label, and renders the trail with the
 * final crumb styled as the active page.
 *
 * Mirrors the legacy UI's `Breadcrumbs.jsx` so visual continuity carries
 * across the re-architecture.
 */
const LABELS: Record<string, string> = {
    dashboard:           'Home',
    properties:          'Properties',
    projects:            'Projects',
    create:              'Create',
    leads:               'Leads',
    'qr-codes':          'QR Codes',
    new:                 'New',
    tours:               'Tours',
    builders:            'Subscribers',
    'sales-team':        'Sales Team',
    compare:             'Compare',
    billing:             'Billing',
    analytics:           'Analytics',
    settings:            'Settings',
    profile:             'Profile',
    '2fa':               'Two-Factor Auth',
    branding:            'Branding',
    terms:               'Terms & Conditions',
    welcome:             'Welcome Sequence',
    'lead-routing':      'Lead Routing',
    audit:               'Audit Log',
    agencies:            'Agency Management',
    'qr-oversight':      'QR Oversight',
    integrations:        'Integrations',
    'integration-health': 'Integration Health',
    subscriptions:       'Subscriptions',
    system:              'System Health',
    security:            'Security',
    schedule:            'Schedule',
    regions:             'Regions',
    help:                'Help',
    plans:               'Plans & Pricing',
    'client-accounts':   'Client Accounts',
};

/** Detail routes put an opaque id in the path — label it instead of echoing it. */
const ID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function humanize(seg: string) {
    if (ID_SEGMENT.test(seg)) return 'Details';
    return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Breadcrumbs({ extra = [] }: Props) {
    const { pathname } = useLocation();
    const parts = pathname.split('/').filter(Boolean);

    const trail: Crumb[] = [];
    let acc = '';
    for (const seg of parts) {
        acc += '/' + seg;
        trail.push({ label: LABELS[seg] ?? humanize(seg), to: acc });
    }
    trail.push(...extra);

    if (trail.length === 0) return null;

    return (
        <nav className="mb-3 flex flex-wrap items-center gap-1.5 font-ui text-xs text-ink-500">
            <Link
                to={DASHBOARD_ROUTES.HOME}
                className="inline-flex items-center gap-1 hover:text-brand-navy"
            >
                <Home className="h-3 w-3" /> PropertyScan
            </Link>
            {trail.map((crumb, i) => {
                const isLast = i === trail.length - 1;
                return (
                    <span key={`${crumb.label}-${i}`} className="inline-flex items-center gap-1.5">
                        <ChevronRight className="h-3 w-3 text-ink-300" />
                        {isLast || !crumb.to ? (
                            <span className="font-bold text-brand-sky-500">{crumb.label}</span>
                        ) : (
                            <Link to={crumb.to} className="hover:text-brand-navy">
                                {crumb.label}
                            </Link>
                        )}
                    </span>
                );
            })}
        </nav>
    );
}
